import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@icetus/ui';
import { Modal } from './overlay';

/**
 * 破坏性操作的确认对话框。
 *
 * 为什么不用 `window.confirm`：
 *
 * 1. **样式对不上**。原生 confirm 是系统弹窗，在深色主题下是一块系统浅色方框，
 *    跟控制台其余部分（深色卡片、圆角、按钮变体）完全不是一套东西。
 *    "视觉统一"这件事上，原生弹窗是最大的缺口。
 * 2. **文案放不下**。原生 confirm 只能给一行纯文本，没法分段、没法加粗强调，
 *    也没法把"会发生什么"讲清楚——而删除恰恰是最需要讲清楚的操作。
 * 3. **不可访问**。原生 confirm 不在 DOM 里，屏幕阅读器读不到按钮标签，
 *    焦点也不会被管理，Esc 之外没有别的退出方式。
 * 4. **会卡住整页**。原生弹窗是浏览器层的模态，会阻塞页面上的所有 JS——
 *    自动化测试、页面内定时器全都停摆。
 *
 * 用法：
 * ```tsx
 * const { confirm, confirmDialog } = useConfirm();
 * const ok = await confirm({ title: '删除商品？', message: '…', tone: 'danger' });
 * if (!ok) return;
 * return <>{confirmDialog}</>;
 * ```
 */

export interface ConfirmOptions {
  title: string;
  /** 正文。可以用 ReactNode，但别放表单控件——确认框里不该再让人做选择。 */
  message: ReactNode;
  /** 主按钮文案，默认「确定」 */
  confirmText?: string;
  /** 次按钮文案，默认「取消」 */
  cancelText?: string;
  /** 危险操作用 danger 按钮（红），默认 primary */
  tone?: 'primary' | 'danger';
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

export function useConfirm(): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  confirmDialog: ReactNode;
} {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  /**
   * resolver 也存一份在 ref 里。
   *
   * 因为 settle 会在 setState 之后立刻调用 resolve，而 setPending(null) 会让
   * Modal 卸载；如果只依赖 state 里的 pending，这个闭包捕获的是旧值，
   * 在 React 18 的批处理下容易出现「对话框关了但 Promise 永远不 resolve」。
   */
  const pendingRef = useRef<PendingConfirm | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      const next: PendingConfirm = { ...options, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(ok);
  }, []);

  const confirmDialog = (
    <Modal
      open={pending !== null}
      onClose={() => settle(false)}
      title={pending?.title ?? ''}
      size="md"
      footer={
        <>
          <Button variant="subtle" onClick={() => settle(false)}>
            {pending?.cancelText ?? '取消'}
          </Button>
          <Button
            variant={pending?.tone === 'danger' ? 'danger' : 'primary'}
            onClick={() => settle(true)}
          >
            {pending?.confirmText ?? '确定'}
          </Button>
        </>
      }
    >
      <div className="confirm__body">{pending?.message}</div>
    </Modal>
  );

  return { confirm, confirmDialog };
}
